import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'

import { useAuth } from '~/auth/auth-context'
import { SearchIcon } from '~/components/icons'
import { SearchFilters } from '~/components/search-filters'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { SetDeck } from '~/components/set-deck'
import { SetGrid } from '~/components/set-grid'
import { useDataState } from '~/data/data-context'
import { searchSets } from '~/data/service'
import { contextHere, readingHere } from '~/lib/set-search-here'
import { describeSearch, readSearch } from '@gymcloud/set-search'
import { useReturnScroll } from '~/lib/opening-set'

interface SearchParams {
  readonly q?: string
}

/** The header's search field owns the input; this page just reads the URL and shows results. */
export const Route = createFileRoute('/search')({
  validateSearch: (search): SearchParams => ({
    ...(typeof search.q === 'string' && search.q.trim() !== '' ? { q: search.q } : {}),
  }),
  component: Search,
})

function Search() {
  const { q } = Route.useSearch()
  const navigate = useNavigate()
  // A set opened from the results flies back to a page standing where it was left.
  useReturnScroll()

  /*
   * Searched off the mirror as it stands, rather than asked once and kept. The mirror fills
   * as the feed lands, and an answer taken at mount and never taken again is how a fresh
   * browser said "No sets match" about a history it had not read yet and went on saying it.
   */
  const state = useDataState()
  // A bare number is a weight in the units the account reads weights in.
  const { units, distanceUnits } = useAuth()
  const results = useMemo(
    () =>
      q === undefined
        ? []
        : searchSets(state.sets, {
            exercise: q,
            units,
            distanceUnits,
            // What a set was part of — its workout and the run it was played under.
            workouts:
              state.currentWorkout === null
                ? state.workouts
                : [...state.workouts, state.currentWorkout],
            enrollments: state.enrollments,
            // Warm-ups are no part of a gallery of sets, results included.
            warmups: false,
          }),
    [state.sets, state.workouts, state.currentWorkout, state.enrollments, q, units, distanceUnits],
  )
  const readAs = useMemo(
    () =>
      q === undefined
        ? []
        : describeSearch(readSearch(q, readingHere()), contextHere(units, distanceUnits)),
    [q, units, distanceUnits],
  )

  if (q === undefined) {
    return (
      <div className="flex flex-col items-center gap-3 py-24 text-center">
        <SearchIcon className="h-10 w-10 text-ink-muted/60" />
        <p className="text-lg font-medium">Search your sets</p>
        <p className="text-sm text-ink-muted">
          An exercise, a note or the numbers — 5x50kg, 8-12 reps, 5km — find every matching set,
          newest first.
        </p>
      </div>
    )
  }

  /*
   * Nothing is said about what was searched for until the sets have been read. "No sets match"
   * off a mirror this browser has not filled is the same false empty state the timeline and
   * the diary refuse to give, and on a browser that cannot reach the account it is permanent.
   */
  if (state.couldNotBeRead) {
    return (
      <div className="py-16 text-center text-ink-muted">
        <p className="text-lg text-ink">Your sets haven’t arrived yet</p>
        <p className="mt-2 text-sm">
          This browser hasn’t been able to reach your account. They will be here once it can.
        </p>
      </div>
    )
  }

  if (!state.timelineSettled) {
    return (
      <SkeletonBlock label="Searching your sets" className="space-y-4">
        <Skeleton className="h-4 w-40" />
        <div className="grid grid-cols-6 gap-1" aria-hidden>
          <Skeleton className="aspect-square" />
          <Skeleton className="aspect-square" />
          <Skeleton className="aspect-square" />
        </div>
      </SkeletonBlock>
    )
  }

  const shown = results

  return (
    <section className="space-y-4">
      <div className="space-y-1 px-0.5">
        <h1 className="text-sm font-medium text-ink-muted">Results for &ldquo;{q}&rdquo;</h1>
        {/*
          How the search was read, said back — the search spec, Saying how search works. It
          stands over the results and over there being none, which is where it explains most.
        */}
        {readAs.length > 0 && <p className="text-sm text-ink-muted/80">{readAs.join(' · ')}</p>}
      </div>

      {/*
        The filters over the results, writing into the search the same as the panel's. Each
        change stands in place of the search it changed rather than behind it, since a range
        writes as it is typed.
      */}
      <SearchFilters
        search={q}
        onSearch={(next) => {
          const trimmed = next.trim()
          void navigate({
            to: '/search',
            search: trimmed === '' ? {} : { q: trimmed },
            replace: true,
          })
        }}
      />

      {shown.length > 0 ? (
        // The timeline's own grid, so results are laid out exactly as the timeline is.
        // The results are also the list a set opened from here is leafed through.
        <SetDeck sets={shown}>
          <SetGrid sets={shown} columns={6} />
        </SetDeck>
      ) : (
        <p className="py-12 text-center text-ink-muted">No sets match.</p>
      )}
    </section>
  )
}
